import { useState } from 'react';
import { Ban } from 'lucide-react';
import { formatTimestampDateTime } from '../../lib/dates';
import { memberDisplayName, type MemberDto } from '../../lib/work-types';
import { Button } from '../ui/button';
import BlockedReasonDialog, { blockedByIdsOf, blockedByNames } from './BlockedReasonDialog';

type BlockedItem = {
  key: string;
  blocked_at?: string | null;
  blocked_reason?: string | null;
  blocked_by_id?: string | null;
  blocked_by_ids?: string[];
  blocked_marked_by_id?: string | null;
};

/** Mark blocked when clear; when blocked, the reason with edit and unblock. */
export default function BlockedControl({
  item,
  members,
  readOnly,
  onPatch,
}: {
  item: BlockedItem;
  members: MemberDto[];
  readOnly: boolean;
  onPatch: (fields: Record<string, unknown>) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const reason = item.blocked_reason ?? null;
  const findMember = (id?: string | null) => (id ? members.find((member) => member.user_id === id) : undefined);
  const blockerIds = blockedByIdsOf(item);
  const blockers = blockedByNames(blockerIds, members);
  const markedBy = findMember(item.blocked_marked_by_id);
  const since = item.blocked_at ? formatTimestampDateTime(item.blocked_at) : null;

  const dialog = (
    <BlockedReasonDialog
      open={open}
      onOpenChange={setOpen}
      workItemKey={item.key}
      members={members}
      initialReason={reason}
      initialBlockedByIds={blockerIds}
      onSubmit={(next) => onPatch({ blocked_reason: next.reason, blocked_by_ids: next.blockedByIds })}
    />
  );

  if (!reason) {
    if (readOnly) return null;
    return (
      <>
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>
          <Ban className="h-4 w-4" aria-hidden />
          Mark blocked
        </Button>
        {dialog}
      </>
    );
  }

  return (
    <div
      role="status"
      className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
    >
      <div className="flex items-start gap-2">
        <Ban className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-300" aria-hidden />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium">Blocked</p>
          <p className="whitespace-pre-wrap break-words">{reason}</p>
          {blockers ? (
            <p>
              <span className="font-medium">Blocked by:</span> {blockers}
            </p>
          ) : null}
          {since || markedBy ? (
            <p className="text-xs text-red-700/80 dark:text-red-200/80">
              {[markedBy ? `Marked by ${memberDisplayName(markedBy)}` : 'Marked', since ? `on ${since}` : null]
                .filter(Boolean)
                .join(' ')}
            </p>
          ) : null}
        </div>
        {readOnly ? null : (
          <div className="flex shrink-0 gap-1.5">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)}>
              Edit
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void onPatch({ blocked_reason: null }).catch(() => undefined)}
            >
              Unblock
            </Button>
          </div>
        )}
      </div>
      {dialog}
    </div>
  );
}
